import { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Instagram, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const contactInfo = [
  {
    icon: Mail,
    title: 'Email Us',
    description: 'For general inquiries and support.',
    value: 'info@aim-hire.org',
    link: 'mailto:info@aim-hire.org',
  },
  {
    icon: Phone,
    title: 'Call Us',
    description: 'Mon-Fri from 9am to 5pm.',
    value: '(917) 246-0621',
    link: 'tel:+19172460621',
  },
  {
    icon: MapPin,
    title: 'Visit Us',
    description: 'Our office location.',
    value: '1010 Broadway, Woodmere, NY 11598',
    link: null,
  },
];

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const formData = new FormData(e.target);
    const data = {
      first_name: formData.get('firstName'),
      last_name: formData.get('lastName'),
      email: formData.get('email'),
      message: formData.get('message'),
    };

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      
      if (response.ok) {
        setSubmitStatus({ type: 'success', message: result.message });
        e.target.reset();
      } else {
        setSubmitStatus({ type: 'error', message: result.detail || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Unable to send message. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative bg-slate-800 overflow-hidden pt-40 md:pt-[calc(12rem+10vh)] pb-12 md:pb-16" data-testid="contact-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700" />
        
        <div className="relative container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="max-w-3xl">
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 animate-fade-in-up"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              <span className="text-purple-200 italic">Get in Touch</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up animation-delay-100">
              Have questions? We're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="contact-content">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Contact Info */}
            <div>
              <div className="space-y-4">
                {contactInfo.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start p-5 bg-slate-50 rounded-xl"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-slate-700" />
                    </div>
                    <div className="ml-3">
                      <h3 
                        className="text-base font-semibold text-slate-900 mb-0.5"
                        style={{ fontFamily: 'var(--font-heading)' }}
                      >
                        {item.title}
                      </h3>
                      <p className="text-xs text-slate-500 mb-1">{item.description}</p>
                      {item.link ? (
                        <a 
                          href={item.link}
                          className="text-sm text-slate-900 font-medium hover:text-slate-700 transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <span className="text-sm text-slate-900 font-medium">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}

                {/* Hours */}
                <div className="flex items-start p-5 bg-slate-50 rounded-xl" data-testid="contact-hours">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-slate-700" />
                  </div>
                  <div className="ml-3">
                    <h3 
                      className="text-base font-semibold text-slate-900 mb-0.5"
                      style={{ fontFamily: 'var(--font-heading)' }}
                    >
                      Office Hours
                    </h3>
                    <p className="text-xs text-slate-500 mb-1">When you can reach us</p>
                    <div className="text-sm text-slate-900 space-y-0.5">
                      <p className="font-medium">Monday - Friday: 9am - 5pm</p>
                      <p className="text-slate-600">Saturday - Sunday: Closed</p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="flex items-start p-5 bg-slate-50 rounded-xl" data-testid="contact-social">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-4 h-4 text-slate-700" />
                  </div>
                  <div className="ml-3">
                    <h3 
                      className="text-base font-semibold text-slate-900 mb-0.5"
                      style={{ fontFamily: 'var(--font-heading)' }}
                    >
                      Follow Us
                    </h3>
                    <p className="text-xs text-slate-500 mb-2">Stay connected on social media</p>
                    <div className="flex items-center gap-3">
                      <a
                        href="https://www.instagram.com/aim_hireorg"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-slate-700 hover:text-purple-600 transition-colors"
                        data-testid="contact-instagram-link"
                      >
                        <Instagram className="w-4 h-4" />
                        <span>Instagram</span>
                      </a>
                      <a
                        href="https://www.linkedin.com/in/aim-hire-recruiting-105708220/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-slate-700 hover:text-purple-600 transition-colors"
                        data-testid="contact-linkedin-link"
                      >
                        <Linkedin className="w-4 h-4" />
                        <span>LinkedIn</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-slate-50 p-6 md:p-8 rounded-xl" data-testid="contact-form">
              <h2 
                className="text-xl font-semibold text-slate-900 mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Send Us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="firstName" className="text-sm">First Name</Label>
                    <Input 
                      id="firstName"
                      name="firstName"
                      placeholder="First name" 
                      className="rounded-md border-slate-200 bg-white"
                      data-testid="contact-firstname-input"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="lastName" className="text-sm">Last Name</Label>
                    <Input 
                      id="lastName"
                      name="lastName"
                      placeholder="Last name" 
                      className="rounded-md border-slate-200 bg-white"
                      data-testid="contact-lastname-input"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="email" className="text-sm">Email</Label>
                  <Input 
                    id="email"
                    name="email"
                    type="email" 
                    placeholder="you@email.com" 
                    className="rounded-md border-slate-200 bg-white"
                    data-testid="contact-email-input"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="message" className="text-sm">Message</Label>
                  <Textarea 
                    id="message"
                    name="message"
                    placeholder="How can we help you?"
                    rows={4}
                    className="rounded-md border-slate-200 bg-white resize-none"
                    data-testid="contact-message-input"
                    required
                  />
                </div>

                {submitStatus && (
                  <div className={`p-3 rounded-lg text-sm ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-5 text-sm disabled:opacity-50"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
